import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useTranslate } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { fileToBase64 } from '../../transforms/fileToBase64';
import type {
  NotificationTestFormValues,
  SelectedFile,
} from '../../types/notificationTest';
import { isSupportedAttachmentMime } from '../../types/notificationTest';

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_FILE_TYPES = 'application/pdf,text/plain,image/*';

interface AttachFileDialogProps {
  open: boolean;
  onClose: () => void;
}

export const AttachFileDialog: React.FC<AttachFileDialogProps> = ({
  open,
  onClose,
}) => {
  const translate = useTranslate();
  const { getValues, setValue } = useFormContext<NotificationTestFormValues>();

  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);

  const resetState = (): void => {
    setFile(null);
    setFileError(undefined);
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const f = e.target.files?.[0] ?? null;
    if (!f) return;
    if (f.size > MAX_FILE_BYTES) {
      setFile(null);
      setFileError(translate('resources.test.attachments.insertImage.errorTooLarge'));
      return;
    }
    if (!isSupportedAttachmentMime(f.type)) {
      setFile(null);
      setFileError(translate('resources.test.attachments.errorUnsupportedType'));
      return;
    }
    setFileError(undefined);
    setFile(f);
  };

  const handleAttach = async (): Promise<void> => {
    if (!file) {
      setFileError(translate('resources.test.attachments.insertImage.errorFileRequired'));
      return;
    }
    setSubmitting(true);
    try {
      const base64 = await fileToBase64(file);
      const newAttachment: SelectedFile = {
        name: file.name,
        type: file.type,
        base64,
        inline: false,
        contentId: '',
      };
      const current = getValues('attachments') ?? [];
      setValue('attachments', [...current, newAttachment], {
        shouldDirty: true,
        shouldValidate: true,
      });
      resetState();
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (): void => {
    resetState();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') return;
        handleClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {translate('resources.test.attachments.attachFile.title')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            type="file"
            onChange={handleFile}
            label={translate('resources.test.attachments.attachFile.fileLabel')}
            InputLabelProps={{ shrink: true }}
            inputProps={{ accept: ACCEPTED_FILE_TYPES }}
            error={Boolean(fileError)}
            helperText={
              fileError ??
              (file
                ? `${file.name} — ${(file.size / 1024).toFixed(1)} KB`
                : translate('resources.test.attachments.attachFile.fileHelper'))
            }
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          {translate('resources.test.actions.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleAttach}
          disabled={submitting || !file}
        >
          {translate('resources.test.attachments.attachFile.attachButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AttachFileDialog;
